// src/components/analytics/ProfitAnalysis.js
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Spin, Alert, DatePicker, Button, Statistic, Space, Typography } from 'antd';
import { DollarOutlined, FileTextOutlined, TrophyOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import analyticsV2Service, { formatDateForAPI } from '../../services/analyticsV2Service';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/formatters';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

function ProfitAnalysis() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
    const [dateRange, setDateRange] = useState(
        isDemoMode
            ? [dayjs('2026-01-01').startOf('day'), dayjs('2026-01-31').endOf('day')]
            : [dayjs().startOf('day'), dayjs().endOf('day')]
    );

    const loadData = async (startDate, endDate) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await analyticsV2Service.getProfitAnalysis(startDate, endDate);
            
            // Check if we have data - API returns data directly in response.data (axios wrapper)
            const apiData = response.data || response;
            
            if (apiData && apiData.totalRevenue !== undefined) {
                // Transform new API structure to match component expectations
                
                // Find invoice types from array
                const facturaA = apiData.invoicesByType?.find(inv => inv.tipoComprobante === 'FACTURA A') || {
                    invoiceCount: 0, totalSales: 0, totalRevenue: 0, netProfitWithoutIVA: 0, ivaCollected: 0, totalProfit: 0
                };
                const facturaB = apiData.invoicesByType?.find(inv => inv.tipoComprobante === 'FACTURA B') || {
                    invoiceCount: 0, totalSales: 0, totalRevenue: 0, netProfitWithoutIVA: 0, ivaCollected: 0, totalProfit: 0
                };
                const presupuesto = apiData.invoicesByType?.find(inv => inv.tipoComprobante === 'PRESUPUESTO') || {
                    invoiceCount: 0, totalSales: 0, totalRevenue: 0, netProfitWithoutIVA: 0, ivaCollected: 0, totalProfit: 0
                };
                
                // Add totalRevenue to each type (use totalSales if not available)
                facturaA.totalRevenue = facturaA.totalRevenue || facturaA.totalSales;
                facturaB.totalRevenue = facturaB.totalRevenue || facturaB.totalSales;
                presupuesto.totalRevenue = presupuesto.totalRevenue || presupuesto.totalSales;
                
                // Calculate total invoices
                const totalInvoices = (facturaA.invoiceCount || 0) + (facturaB.invoiceCount || 0) + (presupuesto.invoiceCount || 0);
                
                // Use backend-provided discount data (more accurate than frontend calculation)
                const totalDiscounts = apiData.totalDiscountsApplied || 0;
                
                // Transform to old structure format
                const transformedData = {
                    summary: {
                        totalRevenue: apiData.totalRevenue,
                        totalSales: apiData.totalSales,
                        totalCost: apiData.totalCost,
                        totalNetProfit: apiData.totalNetProfit,
                        totalTotalProfit: apiData.totalTotalProfit,
                        totalIVACollected: apiData.totalIVACollected,
                        totalDiscounts: totalDiscounts,
                        totalInvoices: totalInvoices,
                        profitMargin: apiData.profitMargin,
                        avgTicket: apiData.avgTicket,
                        facturaA: facturaA,
                        facturaB: facturaB,
                        presupuesto: presupuesto,
                        invoicesWithEstimatedCost: apiData.estimatedCostInfo?.invoicesWithEstimatedCost || 0,
                        salesWithEstimatedCost: apiData.estimatedCostInfo?.salesWithEstimatedCost || 0,
                        totalProfitLost: apiData.creditNotes?.amount || 0,
                        creditNotesCount: apiData.creditNotes?.count || 0
                    }
                };
                
                console.log('[ProfitAnalysis] RAW API Data:', apiData);
                console.log('[ProfitAnalysis] InvoicesByType detail:', apiData.invoicesByType);
                console.log('[ProfitAnalysis] Data loaded successfully:', transformedData);
                
                console.log('[ProfitAnalysis] DISCOUNT ANALYSIS:', {
                    totalRevenue: apiData.totalRevenue,
                    totalSales: apiData.totalSales,
                    totalIVACollected: apiData.totalIVACollected,
                    totalDiscountsAPI: apiData.totalDiscountsApplied,
                    totalDiscounts: totalDiscounts,
                    facturaA: {
                        base: facturaA.totalSales,
                        iva: facturaA.ivaCollected,
                        revenue: facturaA.totalRevenue,
                        theoretical: facturaA.totalRevenue + (facturaA.totalDiscountApplied || 0),
                        discount: facturaA.totalDiscountApplied || 0,
                        discountRate: ((facturaA.totalDiscountApplied || 0) / (facturaA.totalRevenue + (facturaA.totalDiscountApplied || 0)) * 100).toFixed(2) + '%'
                    },
                    facturaB: {
                        base: facturaB.totalSales,
                        iva: facturaB.ivaCollected,
                        revenue: facturaB.totalRevenue,
                        theoretical: facturaB.totalRevenue + (facturaB.totalDiscountApplied || 0),
                        discount: facturaB.totalDiscountApplied || 0,
                        discountRate: ((facturaB.totalDiscountApplied || 0) / (facturaB.totalRevenue + (facturaB.totalDiscountApplied || 0)) * 100).toFixed(2) + '%'
                    },
                    presupuesto: {
                        base: presupuesto.totalSales,
                        iva: presupuesto.ivaCollected,
                        revenue: presupuesto.totalRevenue,
                        theoretical: presupuesto.totalRevenue + (presupuesto.totalDiscountApplied || 0),
                        discount: presupuesto.totalDiscountApplied || 0,
                        discountRate: ((presupuesto.totalDiscountApplied || 0) / (presupuesto.totalRevenue + (presupuesto.totalDiscountApplied || 0)) * 100).toFixed(2) + '%'
                    }
                });
                setData(transformedData);
            } else {
                console.error('[ProfitAnalysis] Invalid response structure:', response);
                setError('No se pudieron cargar los datos de rentabilidad');
            }
        } catch (err) {
            console.error('[ProfitAnalysis] Error loading profit analysis:', err);
            setError(err.response?.data?.error || 'Error al cargar análisis de rentabilidad');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const startDate = formatDateForAPI(dateRange[0].toDate());
        const endDate = formatDateForAPI(dateRange[1].toDate());
        loadData(startDate, endDate);
    }, []);

    const handleDateChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange(dates);
            const startDate = formatDateForAPI(dates[0].toDate());
            const endDate = formatDateForAPI(dates[1].toDate());
            loadData(startDate, endDate);
        }
    };

    const handlePresetClick = (preset) => {
        let start, end;
        const today = dayjs();

        switch (preset) {
            case 'today':
                start = today.startOf('day');
                end = today;
                break;
            case 'week':
                start = today.startOf('week');
                end = today;
                break;
            case 'month':
                start = today.startOf('month');
                end = today;
                break;
            case 'lastMonth':
                start = today.subtract(1, 'month').startOf('month');
                end = today.subtract(1, 'month').endOf('month');
                break;
            case 'year':
                start = today.startOf('year');
                end = today;
                break;
            default:
                return;
        }

        setDateRange([start, end]);
        loadData(formatDateForAPI(start.toDate()), formatDateForAPI(end.toDate()));
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Cargando análisis de rentabilidad..." />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="Error al cargar datos"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
            />
        );
    }

    if (!data) {
        return null;
    }

    const { summary } = data;

    // Prepare chart data - Percentage composition showing theoretical revenue breakdown (including discounts)
    const prepareChartData = (type, typeData) => {
        const discountApplied = typeData.totalDiscountApplied || 0;
        const theoretical = typeData.totalRevenue + discountApplied;  // What it would be without discounts
        const netProfit = typeData.netProfitWithoutIVA;
        const iva = typeData.ivaCollected;
        const costo = theoretical - netProfit - iva - discountApplied;
        
        return {
            name: type,
            'Ganancia Neta': theoretical > 0 ? (netProfit / theoretical) * 100 : 0,
            'Costo': theoretical > 0 ? (costo / theoretical) * 100 : 0,
            'IVA': theoretical > 0 ? (iva / theoretical) * 100 : 0,
            'Descuento': theoretical > 0 ? (discountApplied / theoretical) * 100 : 0,
            // Store absolute values for tooltip
            netProfitAbs: netProfit,
            costoAbs: costo,
            ivaAbs: iva,
            discountAbs: discountApplied,
            totalTheoretical: theoretical,
            totalRevenue: typeData.totalRevenue,
            totalSales: typeData.totalSales,
            invoices: typeData.invoiceCount
        };
    };
    
    const chartData = [
        prepareChartData('Factura A', summary.facturaA),
        prepareChartData('Factura B', summary.facturaB),
        prepareChartData('Factura X', summary.presupuesto)
    ];

    // Table data
    const tableData = [
        {
            key: '1',
            type: 'Factura A',
            count: summary.facturaA.invoiceCount,
            totalSales: summary.facturaA.totalSales,
            costo: summary.facturaA.totalSales - summary.facturaA.netProfitWithoutIVA,
            iva: summary.facturaA.ivaCollected,
            discount: summary.facturaA.totalDiscountApplied || 0,
            totalRevenue: summary.facturaA.totalRevenue || 0,
            total: summary.facturaA.totalProfit,
            netProfit: summary.facturaA.netProfitWithoutIVA
        },
        {
            key: '2',
            type: 'Factura B',
            count: summary.facturaB.invoiceCount,
            totalSales: summary.facturaB.totalSales,
            costo: summary.facturaB.totalSales - summary.facturaB.netProfitWithoutIVA,
            iva: summary.facturaB.ivaCollected,
            discount: summary.facturaB.totalDiscountApplied || 0,
            totalRevenue: summary.facturaB.totalRevenue || 0,
            total: summary.facturaB.totalProfit,
            netProfit: summary.facturaB.netProfitWithoutIVA
        },
        {
            key: '3',
            type: 'Factura X',
            count: summary.presupuesto.invoiceCount,
            totalSales: summary.presupuesto.totalSales,
            costo: summary.presupuesto.totalSales - summary.presupuesto.netProfitWithoutIVA,
            iva: summary.presupuesto.ivaCollected,
            discount: summary.presupuesto.totalDiscountApplied || 0,
            totalRevenue: summary.presupuesto.totalRevenue || 0,
            total: summary.presupuesto.totalProfit,
            netProfit: summary.presupuesto.netProfitWithoutIVA
        }
    ];

    const columns = [
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (value) => <Text style={{ fontSize: '18px' }}>{value}</Text>
        },
        {
            title: 'Cantidad',
            dataIndex: 'count',
            key: 'count',
            align: 'right',
            width: 90,
            render: (value) => <Text style={{ fontSize: '18px' }}>{formatNumber(value)}</Text>
        },
        {
            title: (
                <div>
                    <div>Base Imponible</div>
                    <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#8c8c8c' }}>(Neto Gravado)</div>
                </div>
            ),
            dataIndex: 'totalSales',
            key: 'totalSales',
            align: 'right',
            render: (value) => <Text style={{ color: '#722ed1', fontSize: '18px' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        },
        {
            title: (
                <div>
                    <div>IVA</div>
                    <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#8c8c8c' }}>(21%)</div>
                </div>
            ),
            dataIndex: 'iva',
            key: 'iva',
            align: 'right',
            render: (value) => <Text type="warning" style={{ fontSize: '18px' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        },
        {
            title: 'Descuento',
            dataIndex: 'discount',
            key: 'discount',
            align: 'right',
            render: (value) => <Text style={{ color: '#9254de', fontSize: '18px' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        },
        {
            title: (
                <div>
                    <div>Ventas Totales</div>
                    <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#8c8c8c' }}>(Total Facturado)</div>
                </div>
            ),
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            align: 'right',
            render: (value) => <Text strong style={{ color: '#1890ff', fontSize: '18px' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        },
        {
            title: (
                <div>
                    <div>Costo</div>
                    <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#8c8c8c' }}>(Costo de Venta)</div>
                </div>
            ),
            dataIndex: 'costo',
            key: 'costo',
            align: 'right',
            render: (value) => <Text style={{ color: '#cf1322', fontSize: '18px' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        },
        {
            title: (
                <div>
                    <div>Ganancia Neta</div>
                    <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#8c8c8c' }}>(sin IVA)</div>
                </div>
            ),
            dataIndex: 'netProfit',
            key: 'netProfit',
            align: 'right',
            render: (value) => <Text strong style={{ fontSize: '20px', color: '#52c41a' }}>{formatCurrency(value, { decimals: 0 })}</Text>
        }
    ];

    const totalIVA = summary.totalIVACollected || (
        summary.facturaA.ivaCollected + 
        summary.facturaB.ivaCollected + 
        summary.presupuesto.ivaCollected
    );

    const profitLostPercent = summary.totalTotalProfit > 0 
        ? (summary.totalProfitLost / summary.totalTotalProfit) * 100 
        : 0;

    return (
        <div style={{ overflow: 'hidden' }}>
            {/* Title and Date Range Selector */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 16,
                padding: '12px 16px',
                background: '#fff',
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
            }}>
                <Space>
                    <DollarOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                    <span style={{ fontSize: '16px', fontWeight: 500 }}>Análisis de Rentabilidad</span>
                </Space>
                {isDemoMode ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 12px', borderRadius: '6px',
                        background: '#e6f7ff', border: '1px solid #91d5ff',
                        color: '#1890ff', fontSize: '13px', fontWeight: 500
                    }}>
                        <CalendarOutlined /> Enero 2026
                    </span>
                ) : (
                    <Space wrap size="small">
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            size="small"
                            style={{ width: 220 }}
                        />
                        <Button size="small" onClick={() => handlePresetClick('today')}>Hoy</Button>
                        <Button size="small" onClick={() => handlePresetClick('week')}>Semana</Button>
                        <Button size="small" onClick={() => handlePresetClick('month')}>Mes</Button>
                        <Button size="small" onClick={() => handlePresetClick('lastMonth')}>Mes Pasado</Button>
                        <Button size="small" onClick={() => handlePresetClick('year')}>Año</Button>
                    </Space>
                )}
            </div>

            {/* Estimated Cost Info Banner */}
            {summary.invoicesWithEstimatedCost > 0 && (
                <Alert
                    message={
                        <span style={{ fontSize: '13px' }}>
                            <strong>Costos Estimados:</strong> {summary.invoicesWithEstimatedCost} facturas ({formatPercentage((summary.invoicesWithEstimatedCost / summary.totalInvoices) * 100, 0, false)}) 
                            usan costos estimados - {formatCurrency(summary.salesWithEstimatedCost, { decimals: 0 })} ({formatPercentage((summary.salesWithEstimatedCost / summary.totalSales) * 100, 0, false)}) 
                            de ventas con margen del 33%.
                        </span>
                    }
                    type="info"
                    showIcon
                    closable
                    style={{ marginBottom: 16, padding: '8px 15px' }}
                />
            )}

            {/* Summary Cards - Reordered: Facturas, Ventas, Ganancia Neta, IVA, NC, Costos */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8} lg={3} xl={3}>
                    <Card size="small" style={{ borderLeft: '4px solid #1890ff' }}>
                        <Statistic
                            title="Facturas"
                            value={summary.totalInvoices}
                            valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            A: {summary.facturaA.invoiceCount} | B: {summary.facturaB.invoiceCount} | X: {summary.presupuesto.invoiceCount}
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                    <Card size="small" style={{ borderLeft: '4px solid #722ed1' }}>
                        <Statistic
                            title="Ventas Totales"
                            value={summary.totalRevenue}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#722ed1', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Facturación total (con IVA)
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={3} xl={3}>
                    <Card size="small" style={{ borderLeft: '4px solid #3f8600' }}>
                        <Statistic
                            title="Ganancia Neta"
                            value={summary.totalNetProfit}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#3f8600', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Descontado Costos e IVA
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={3} xl={3}>
                    <Card size="small" style={{ borderLeft: '4px solid #fa8c16' }}>
                        <Statistic
                            title="IVA a Pagar"
                            value={totalIVA}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#fa8c16', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Impuesto a AFIP
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                    <Card size="small" style={{ borderLeft: '4px solid #cf1322' }}>
                        <Statistic
                            title="Notas de Crédito"
                            value={summary.totalProfitLost}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#cf1322', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            En {summary.creditNotesCount} Notas de Crédito ({formatPercentage(profitLostPercent, 0, false)})
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                    <Card size="small" style={{ borderLeft: '4px solid #722ed1' }}>
                        <Statistic
                            title="Descuentos Aplicados"
                            value={summary.totalDiscounts}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#722ed1', fontSize: '24px' }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {formatPercentage((summary.totalDiscounts / (summary.totalSales + totalIVA)) * 100, 1, false)} de descuento
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={3} xl={3}>
                    <Card size="small" style={{ borderLeft: '4px solid #13c2c2' }}>
                        <Statistic
                            title="Costos Estimados"
                            value={summary.invoicesWithEstimatedCost || 0}
                            valueStyle={{ color: '#13c2c2', fontSize: '24px' }}
                            suffix={`/ ${summary.totalInvoices}`}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {summary.invoicesWithEstimatedCost > 0 
                                ? `${formatPercentage((summary.invoicesWithEstimatedCost / summary.totalInvoices) * 100, 0, false)} con costos estimados`
                                : 'Todos con costos reales'
                            }
                        </Text>
                    </Card>
                </Col>
            </Row>

            {/* Chart and Table - Side by Side */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} lg={8}>
                    <Card title="Composición de Ventas por Tipo (%)" size="small">
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    type="number"
                                    style={{ fontSize: '12px' }} 
                                    tickFormatter={(value) => `${value.toFixed(0)}%`}
                                    domain={[0, 100]}
                                />
                                <YAxis 
                                    type="category"
                                    dataKey="name" 
                                    style={{ fontSize: '12px' }}
                                    width={80}
                                />
                                <Tooltip 
                                    formatter={(value, name, props) => {
                                        const { payload } = props;
                                        let absoluteValue;
                                        
                                        if (name === 'Ganancia Neta') {
                                            absoluteValue = payload.netProfitAbs;
                                        } else if (name === 'Costo') {
                                            absoluteValue = payload.costoAbs;
                                        } else if (name === 'IVA') {
                                            absoluteValue = payload.ivaAbs;
                                        } else if (name === 'Descuento') {
                                            absoluteValue = payload.discountAbs;
                                        }
                                        
                                        return [
                                            `${value.toFixed(1)}% (${formatCurrency(absoluteValue)})`,
                                            name
                                        ];
                                    }}
                                    labelFormatter={(label) => `${label}`}
                                />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                                <Bar dataKey="Ganancia Neta" stackId="a" fill="#52c41a" />
                                <Bar dataKey="Costo" stackId="a" fill="#8c8c8c" />
                                <Bar dataKey="IVA" stackId="a" fill="#fa8c16" />
                                <Bar dataKey="Descuento" stackId="a" fill="#9254de" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                
                <Col xs={24} lg={16}>
                    <Card title="Detalle por Tipo" size="small">
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary>
                                    <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                        <Table.Summary.Cell><Text style={{ fontSize: '18px' }}>TOTAL</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text style={{ fontSize: '18px' }}>{formatNumber(summary.totalInvoices)}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text style={{ color: '#722ed1', fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(summary.totalSales, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text type="warning" style={{ fontSize: '18px' }}>{formatCurrency(totalIVA, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text style={{ color: '#9254de', fontSize: '18px' }}>{formatCurrency(summary.totalDiscounts, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text strong style={{ color: '#1890ff', fontSize: '18px' }}>{formatCurrency(summary.totalRevenue, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text style={{ color: '#cf1322', fontSize: '18px' }}>{formatCurrency(summary.totalCost, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text strong style={{ fontSize: '20px', color: '#52c41a' }}>{formatCurrency(summary.totalNetProfit, { decimals: 0 })}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                        
                        {/* Compact Info */}
                        <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#f0f2f5', borderRadius: 4 }}>
                            <Text style={{ fontSize: 11 }}>
                                💡 <strong>Ganancia Neta:</strong> Base Imponible - Costo.
                            </Text>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default ProfitAnalysis;
